// pages/LogicalPlugins.jsx
// Logical Canvas — Authority Portal (ERP Control Plane)
// This portal sits ABOVE all application sites (Finance, Sales, CRM)
// It defines: Trust, Access, Capabilities, Policies, and Artifact Flow

import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import LogicalPluginsModal from "../shared/components/LogicalPluginsModal.jsx";
import PolicyPanel from "../shared/components/PolicyPanel.jsx";
import LogicalCanvasSites from "./LogicalCanvasSites.jsx";
import { resolveEffectivePolicy, fetchUpstreamPolicy } from "../lib/logicalCanvas/policyEngine.js";
import { loadSelection } from "../lib/logicalCanvas/storage.js";
import { getAllCapabilities, getCapabilitiesBySite } from "../lib/logicalCanvas/pluginRegistry.js";

export default function LogicalPlugins() {
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab");
  
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState(tabFromUrl || "sites"); // Sites is default, or from URL
  const [activeLayer, setActiveLayer] = useState("features");
  const [effectivePolicy, setEffectivePolicy] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeServicePath, setActiveServicePath] = useState(null);
  
  // Active Site State (Single Source of Truth)
  const [activeSite, setActiveSite] = useState(() => {
    // Load from localStorage on init
    const stored = localStorage.getItem("lc.activeSite.v0");
    return stored || "finance"; // Default to finance
  });

  useEffect(() => {
    loadPolicies();
  }, [activeSite]); // Reload policies when site changes
  
  // Persist activeSite to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("lc.activeSite.v0", activeSite);
  }, [activeSite]);

  async function loadPolicies() {
    setLoading(true);
    const upstream = await fetchUpstreamPolicy();
    const local = loadSelection();
    // CRITICAL: Filter capabilities by activeSite - this enforces site scoping
    const registry = getCapabilitiesBySite(activeSite);
    const effective = resolveEffectivePolicy(upstream, local, registry);
    setEffectivePolicy(effective);
    setLoading(false);
  }

  const handleModalClose = () => {
    setShowModal(false);
    loadPolicies(); // Reload after modal closes to reflect any changes
  };
  
  const handleSiteChange = (siteId) => {
    setActiveSite(siteId);
    // Switching site should take user to the features layer
    setActiveTab("plugins");
  };

  // Layered Portal Model
  const layers = [
    { key: "security", label: "Layer 1: Security", subtitle: "Trust & Authentication", disabled: false },
    { key: "access", label: "Layer 2: Access", subtitle: "Read-Only Visibility", disabled: false },
    { key: "features", label: "Layer 3: Features", subtitle: "Capabilities", disabled: false },
    { key: "policies", label: "Layer 4: Policies", subtitle: "Terms & Governance", disabled: false },
  ];

  // Service Paths (ALL SITES)
  const servicePaths = [
    { key: "permissions", label: "Permission Requests", icon: "🔐", description: "Request access or elevation" },
    { key: "documents", label: "Document Submission", icon: "📄", description: "Submit artifacts for approval" },
    { key: "issues", label: "Issue / Complaint", icon: "⚠️", description: "Report issues or concerns" },
  ];

  const enabledCount = Object.values(effectivePolicy).filter(p => p.enabled).length;
  const totalCount = Object.keys(effectivePolicy).length;

  return (
    <div className="min-h-screen bg-[#070e12] text-ice">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="text-mist uppercase tracking-[.25em] text-xs">
            Authority Portal • Zero Trust • Artifact Flow
          </div>
          <h1 className="text-ice text-3xl font-semibold mt-1">Logical Canvas</h1>
          <p className="text-mist/80 mt-2">
            ERP Control Plane — Sits ABOVE all application sites. Defines trust, access, capabilities, and policies.
          </p>
          <div className="mt-3 flex gap-3 text-xs">
            <span className="text-mist/60">🔒 Zero Trust by Default</span>
            <span className="text-mist/60">•</span>
            <span className="text-mist/60">📦 Artifact-Based Flow</span>
            <span className="text-mist/60">•</span>
            <span className="text-mist/60">✅ Explicit Approval Required</span>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="mb-8">
          <div className="flex gap-2 border-b border-edge pb-2">
            <button
              onClick={() => setActiveTab("sites")}
              className={`px-4 py-2 rounded-t transition-all ${
                activeTab === "sites"
                  ? "bg-[#1a2c36] border-ice text-ice font-semibold"
                  : "text-mist hover:text-ice hover:bg-[#0e1b22]"
              }`}
            >
              Sites
            </button>
            <button
              onClick={() => setActiveTab("plugins")}
              disabled={!activeSite}
              className={`px-4 py-2 rounded-t transition-all ${
                activeTab === "plugins"
                  ? "bg-[#1a2c36] border-ice text-ice font-semibold"
                  : !activeSite
                  ? "text-mist/40 cursor-not-allowed"
                  : "text-mist hover:text-ice hover:bg-[#0e1b22]"
              }`}
            >
              Plugins
            </button>
            <button
              onClick={() => setActiveTab("policies")}
              disabled={!activeSite}
              className={`px-4 py-2 rounded-t transition-all ${
                activeTab === "policies"
                  ? "bg-[#1a2c36] border-ice text-ice font-semibold"
                  : !activeSite
                  ? "text-mist/40 cursor-not-allowed"
                  : "text-mist hover:text-ice hover:bg-[#0e1b22]"
              }`}
            >
              Policies
            </button>
            <button
              onClick={() => setActiveTab("admin")}
              className={`px-4 py-2 rounded-t transition-all ${
                activeTab === "admin"
                  ? "bg-[#1a2c36] border-ice text-ice font-semibold"
                  : "text-mist hover:text-ice hover:bg-[#0e1b22]"
              }`}
            >
              Admin
            </button>
          </div>
        </div>
        
        {/* Layered Portal Model (only show when on plugins tab) */}
        {activeTab === "plugins" && (
          <div className="mb-8">
            <h2 className="text-ice text-lg font-semibold mb-3">Layered Portal Model</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              {layers.map(layer => (
                <button
                  key={layer.key}
                  onClick={() => !layer.disabled && setActiveLayer(layer.key)}
                  disabled={layer.disabled}
                  className={`text-left p-4 rounded border transition-all ${
                    activeLayer === layer.key
                      ? "bg-[#1a2c36] border-ice text-ice"
                      : layer.disabled
                      ? "surface border-edge/50 text-mist/40 cursor-not-allowed"
                      : "surface border-edge text-mist hover:border-ice/50 hover:text-ice"
                  }`}
                >
                  <div className="font-semibold text-sm">{layer.label}</div>
                  <div className="text-xs mt-1 opacity-80">{layer.subtitle}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Sites Tab Content */}
        {activeTab === "sites" && (
          <LogicalCanvasSites activeSite={activeSite} onSiteChange={handleSiteChange} />
        )}
        
        {/* Service Paths (show when on plugins or admin tab) */}
        {(activeTab === "plugins" || activeTab === "admin") && (
          <div className="mb-8 surface rounded-hard p-5">
            <h2 className="text-ice text-lg font-semibold mb-3">Service Paths (All Sites)</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {servicePaths.map(path => (
              <button
                key={path.key}
                onClick={() => setActiveServicePath(path.key)}
                className="text-left p-4 rounded border border-edge hover:border-ice/50 hover:bg-[#0e1b22] transition-all"
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">{path.icon}</span>
                  <span className="text-ice font-semibold">{path.label}</span>
                </div>
                <p className="text-mist/70 text-sm">{path.description}</p>
              </button>
            ))}
          </div>
        </div>
        )}

        {/* Layer Content (only show when on plugins tab) */}
        
        {/* Layer 1: Security (Trust) */}
        {activeTab === "plugins" && activeLayer === "security" && (
          <div className="surface rounded-hard p-6">
            <h2 className="text-ice text-xl font-semibold mb-2">Layer 1: Security & Trust</h2>
            <p className="text-mist/80 mb-6">Authentication required for all users. No implicit trust between sites.</p>
            
            <div className="space-y-4">
              <div className="border border-edge rounded p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-ice font-semibold">Authentication Status</span>
                  <span className="px-3 py-1 rounded text-xs bg-[#1f3d29] text-[#7fd199]">Authenticated (Mock)</span>
                </div>
                <p className="text-mist/70 text-sm">Identity validated before any action. Zero trust by default.</p>
              </div>

              <div className="border border-edge rounded p-4">
                <div className="text-ice font-semibold mb-2">Trust Boundaries</div>
                <ul className="list-disc pl-5 space-y-1 text-mist/70 text-sm">
                  <li>No cross-site database access</li>
                  <li>No shared credentials between applications</li>
                  <li>All inter-site communication via approved artifacts</li>
                  <li>Identity validation required for every session</li>
                </ul>
              </div>

              <div className="border border-edge rounded p-4 bg-[#3d2020]/20">
                <div className="text-ice font-semibold mb-2">⚠️ Frontend-Only Notice</div>
                <p className="text-mist/70 text-sm">
                  Authentication is simulated. Production requires: SSO, RBAC, session management, and audit logging.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Layer 2: Access (Read-Only Visibility) */}
        {activeTab === "plugins" && activeLayer === "access" && (
          <div className="surface rounded-hard p-6">
            <h2 className="text-ice text-xl font-semibold mb-2">Layer 2: Access (Read-Only Visibility)</h2>
            <p className="text-mist/80 mb-6">Centralized viewing of approved data. View-only. No direct database queries.</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border border-edge rounded p-4">
                <div className="text-ice font-semibold mb-3">Sales Data</div>
                <div className="text-mist/60 text-sm mb-2">View-only access to approved sales artifacts</div>
                <div className="flex items-center gap-2 text-xs text-mist/50">
                  <span>👁️ Read-Only</span>
                  <span>•</span>
                  <span>0 documents available</span>
                </div>
              </div>

              <div className="border border-edge rounded p-4">
                <div className="text-ice font-semibold mb-3">CRM Invoices</div>
                <div className="text-mist/60 text-sm mb-2">Approved invoices from CRM system</div>
                <div className="flex items-center gap-2 text-xs text-mist/50">
                  <span>👁️ Read-Only</span>
                  <span>•</span>
                  <span>0 invoices available</span>
                </div>
              </div>

              <div className="border border-edge rounded p-4">
                <div className="text-ice font-semibold mb-3">Approved Documents</div>
                <div className="text-mist/60 text-sm mb-2">Authority-approved artifacts only</div>
                <div className="flex items-center gap-2 text-xs text-mist/50">
                  <span>👁️ Read-Only</span>
                  <span>•</span>
                  <span>0 documents in authority store</span>
                </div>
              </div>

              <div className="border border-edge rounded p-4">
                <div className="text-ice font-semibold mb-3">Approval Status</div>
                <div className="text-mist/60 text-sm mb-2">Track submitted document states</div>
                <div className="flex items-center gap-2 text-xs text-mist/50">
                  <span>👁️ Read-Only</span>
                  <span>•</span>
                  <span>0 pending approvals</span>
                </div>
              </div>
            </div>

            <div className="mt-4 border border-edge rounded p-4 bg-[#0a1318]">
              <div className="text-ice font-semibold mb-2">Approved Database (Authority Store)</div>
              <p className="text-mist/70 text-sm">
                Separate secure store containing only approved documents, sanitized reference data, and immutable artifacts.
                Functions like a protected main branch. No raw transactional access.
              </p>
            </div>
          </div>
        )}

        {/* Layer 3: Features (Capabilities) */}
        {activeTab === "plugins" && activeLayer === "features" && (
          <div className="space-y-6">
            <div className="surface rounded-hard p-6">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-ice text-xl font-semibold">Layer 3: Features (Capabilities)</h2>
                  <p className="text-mist/80 mt-1">
                    Features are enabled, read-only, or locked. Changes require admin approval.
                    Sites cannot self-enable features from other systems.
                  </p>
                  <div className="mt-4 flex items-center gap-4">
                    <div className="text-sm">
                      <span className="text-mist">Enabled:</span>{" "}
                      <span className="text-ice font-semibold">{enabledCount}</span>
                    </div>
                    <div className="text-mist/50">•</div>
                    <div className="text-sm">
                      <span className="text-mist">Total:</span>{" "}
                      <span className="text-ice font-semibold">{totalCount}</span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setShowModal(true)}
                  className="chip px-6 py-3 bg-[#2a4456] hover:bg-[#345566] font-medium"
                >
                  Manage Capabilities
                </button>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="surface rounded p-4">
                <div className="text-mist text-sm">Read/Write Access</div>
                <div className="text-ice text-2xl font-semibold mt-1">
                  {Object.values(effectivePolicy).filter(p => p.enabled && p.mode === "read-write").length}
                </div>
              </div>
              <div className="surface rounded p-4">
                <div className="text-mist text-sm">Read-Only Access</div>
                <div className="text-ice text-2xl font-semibold mt-1">
                  {Object.values(effectivePolicy).filter(p => p.enabled && p.mode === "read-only").length}
                </div>
              </div>
              <div className="surface rounded p-4">
                <div className="text-mist text-sm">Locked by Authority</div>
                <div className="text-ice text-2xl font-semibold mt-1">
                  {Object.values(effectivePolicy).filter(p => p.locked).length}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Layer 4: Policies & Terms */}
        {activeTab === "plugins" && activeLayer === "policies" && (
          <div className="surface rounded-hard p-6">
            <div className="mb-4">
              <h2 className="text-ice text-xl font-semibold">Layer 4: Policies & Terms</h2>
              <p className="text-mist/80 mt-1">
                Read-only policies explaining why access is limited, which policy enforces it, and applicable terms of use.
                Improves clarity and governance.
              </p>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="surface rounded p-4">
                <div className="text-mist text-sm">Read/Write Access</div>
                <div className="text-ice text-2xl font-semibold mt-1">
                  {Object.values(effectivePolicy).filter(p => p.enabled && p.mode === "read-write").length}
                </div>
              </div>
              <div className="surface rounded p-4">
                <div className="text-mist text-sm">Read-Only Access</div>
                <div className="text-ice text-2xl font-semibold mt-1">
                  {Object.values(effectivePolicy).filter(p => p.enabled && p.mode === "read-only").length}
                </div>
              </div>
              <div className="surface rounded p-4">
                <div className="text-mist text-sm">Enforced by Policy</div>
                <div className="text-ice text-2xl font-semibold mt-1">
                  {Object.values(effectivePolicy).filter(p => p.locked).length}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "policies" && (
          <div className="surface rounded-hard p-6">
            <div className="mb-4">
              <h2 className="text-ice text-xl font-semibold">Effective Policies</h2>
              <p className="text-mist/80 mt-1">
                View the resolved policy state for all capabilities. Policies are resolved from
                upstream governance, local preferences, and registry defaults.
              </p>
            </div>
            {loading ? (
              <div className="text-mist text-center py-8">Loading policies...</div>
            ) : (
              <PolicyPanel effectivePolicy={effectivePolicy} />
            )}
          </div>
        )}
      </div>

      {/* Capability Management Modal */}
      {showModal && <LogicalPluginsModal onClose={handleModalClose} activeSite={activeSite} />}

      {/* Service Path Modals */}
      {activeServicePath === "permissions" && (
        <ServicePathPermissions onClose={() => setActiveServicePath(null)} />
      )}
      {activeServicePath === "documents" && (
        <ServicePathDocuments onClose={() => setActiveServicePath(null)} />
      )}
      {activeServicePath === "issues" && (
        <ServicePathIssues onClose={() => setActiveServicePath(null)} />
      )}
    </div>
  );
}

// Service Path 1: Permission Requests
function ServicePathPermissions({ onClose }) {
  const [requestType, setRequestType] = useState("capability");
  const [justification, setJustification] = useState("");

  const handleSubmit = () => {
    console.log("Permission request submitted:", { requestType, justification });
    alert("Permission request submitted. Admin approval required.");
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="surface relative w-[min(700px,92vw)] rounded-hard p-6 z-10">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-ice text-lg font-semibold">🔐 Permission Request</h3>
          <button className="chip px-3 py-1" onClick={onClose}>Close</button>
        </div>

        <p className="text-mist/80 text-sm mb-4">
          Request access or elevation. Admin-controlled. No self-granting.
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-ice text-sm mb-2">Request Type</label>
            <select
              value={requestType}
              onChange={(e) => setRequestType(e.target.value)}
              className="w-full bg-[#0e1b22] border border-edge rounded px-3 py-2 text-ice"
            >
              <option value="capability">Enable Capability</option>
              <option value="elevation">Elevate Access Mode</option>
              <option value="admin">Admin Rights</option>
            </select>
          </div>

          <div>
            <label className="block text-ice text-sm mb-2">Justification</label>
            <textarea
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              className="w-full bg-[#0e1b22] border border-edge rounded px-3 py-2 text-ice"
              rows="4"
              placeholder="Explain why you need this permission..."
            />
          </div>

          <div className="border border-edge rounded p-3 bg-[#3d2020]/20 text-sm">
            <div className="text-ice font-semibold mb-1">⚠️ Explicit Approval Required</div>
            <p className="text-mist/70">
              All permission requests are logged and require admin approval. No self-granting.
            </p>
          </div>

          <div className="flex justify-end gap-2">
            <button className="chip px-4 py-2" onClick={onClose}>Cancel</button>
            <button
              className="chip px-4 py-2 bg-[#2a4456] hover:bg-[#345566]"
              onClick={handleSubmit}
              disabled={!justification.trim()}
            >
              Submit Request
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Service Path 2: Document Submission
function ServicePathDocuments({ onClose }) {
  const [docType, setDocType] = useState("report");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  const handleSubmit = () => {
    console.log("Document submitted:", { docType, title, description });
    alert("Document submitted for approval. State: draft → pending approval");
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="surface relative w-[min(700px,92vw)] rounded-hard p-6 z-10">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-ice text-lg font-semibold">📄 Document Submission</h3>
          <button className="chip px-3 py-1" onClick={onClose}>Close</button>
        </div>

        <p className="text-mist/80 text-sm mb-4">
          Documents move as artifacts. States: draft → approved → submitted → adopted (authority-controlled).
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-ice text-sm mb-2">Document Type</label>
            <select
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              className="w-full bg-[#0e1b22] border border-edge rounded px-3 py-2 text-ice"
            >
              <option value="report">Financial Report</option>
              <option value="invoice">Invoice Reference</option>
              <option value="sales">Sales Data</option>
              <option value="other">Other Artifact</option>
            </select>
          </div>

          <div>
            <label className="block text-ice text-sm mb-2">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full bg-[#0e1b22] border border-edge rounded px-3 py-2 text-ice"
              placeholder="Document title..."
            />
          </div>

          <div>
            <label className="block text-ice text-sm mb-2">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full bg-[#0e1b22] border border-edge rounded px-3 py-2 text-ice"
              rows="4"
              placeholder="Describe the document and its purpose..."
            />
          </div>

          <div className="border border-edge rounded p-3 bg-[#0a1318] text-sm">
            <div className="text-ice font-semibold mb-1">Artifact Flow Model</div>
            <p className="text-mist/70">
              Documents persist across pages and are used for reports and cross-site sharing.
              Immutable once approved. Stored in Authority Database.
            </p>
          </div>

          <div className="flex justify-end gap-2">
            <button className="chip px-4 py-2" onClick={onClose}>Cancel</button>
            <button
              className="chip px-4 py-2 bg-[#2a4456] hover:bg-[#345566]"
              onClick={handleSubmit}
              disabled={!title.trim() || !description.trim()}
            >
              Submit Document
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Service Path 3: Issue / Complaint Submission
function ServicePathIssues({ onClose }) {
  const [issueType, setIssueType] = useState("data");
  const [linkedDoc, setLinkedDoc] = useState("");
  const [description, setDescription] = useState("");

  const handleSubmit = () => {
    console.log("Issue submitted:", { issueType, linkedDoc, description });
    alert("Issue submitted. Immutable after submission. Routed to Admin/Security/Authority.");
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="surface relative w-[min(700px,92vw)] rounded-hard p-6 z-10">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-ice text-lg font-semibold">⚠️ Issue / Complaint Submission</h3>
          <button className="chip px-3 py-1" onClick={onClose}>Close</button>
        </div>

        <p className="text-mist/80 text-sm mb-4">
          Submit issues with linked documents and descriptions. Immutable after submission.
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-ice text-sm mb-2">Issue Type</label>
            <select
              value={issueType}
              onChange={(e) => setIssueType(e.target.value)}
              className="w-full bg-[#0e1b22] border border-edge rounded px-3 py-2 text-ice"
            >
              <option value="data">Data Integrity Issue</option>
              <option value="access">Unauthorized Access</option>
              <option value="policy">Policy Violation</option>
              <option value="security">Security Concern</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-ice text-sm mb-2">Linked Document (Optional)</label>
            <input
              type="text"
              value={linkedDoc}
              onChange={(e) => setLinkedDoc(e.target.value)}
              className="w-full bg-[#0e1b22] border border-edge rounded px-3 py-2 text-ice"
              placeholder="Document ID or reference..."
            />
          </div>

          <div>
            <label className="block text-ice text-sm mb-2">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full bg-[#0e1b22] border border-edge rounded px-3 py-2 text-ice"
              rows="5"
              placeholder="Describe the issue in detail..."
            />
          </div>

          <div className="border border-edge rounded p-3 bg-[#3d2020]/20 text-sm">
            <div className="text-ice font-semibold mb-1">⚠️ Immutable Submission</div>
            <p className="text-mist/70">
              Once submitted, this issue cannot be edited or deleted. It will be routed to
              Admin, Security, or Authority personnel for review.
            </p>
          </div>

          <div className="flex justify-end gap-2">
            <button className="chip px-4 py-2" onClick={onClose}>Cancel</button>
            <button
              className="chip px-4 py-2 bg-[#2a4456] hover:bg-[#345566]"
              onClick={handleSubmit}
              disabled={!description.trim()}
            >
              Submit Issue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
